import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Set in the admin panel so the owner's own browsing doesn't skew the numbers.
export const EXCLUDE_KEY = 'binud:exclude-analytics';

const BOT = /bot|crawl|spider|slurp|headless|lighthouse|preview/i;

const randomId = () =>
  window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

// Reads a value from local/session storage, creating it on first use. Storage can throw
// (private mode, blocked cookies), in which case the visit still counts with a one-off id.
const stored = (getStorage, key, make) => {
  try {
    const storage = getStorage();
    let value = storage.getItem(key);
    if (!value) {
      value = make();
      storage.setItem(key, value);
    }
    return value;
  } catch {
    return make();
  }
};

export const isExcluded = () => {
  try {
    return window.localStorage.getItem(EXCLUDE_KEY) === '1';
  } catch {
    return false;
  }
};

export const setExcluded = (excluded) => {
  try {
    window.localStorage.setItem(EXCLUDE_KEY, excluded ? '1' : '0');
  } catch {
    // Storage unavailable: nothing to remember.
  }
};

// The first time the owner signs in on a browser, stop counting that browser
// (they can switch it back on from the Analytics page).
export const excludeByDefault = () => {
  try {
    if (window.localStorage.getItem(EXCLUDE_KEY) === null) setExcluded(true);
  } catch {
    // Storage unavailable: nothing to remember.
  }
};

const parseAgent = (ua) => {
  const tablet = /iPad|Tablet/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua));
  const device = tablet ? 'Tablet' : /Mobi|iPhone|Android/i.test(ua) ? 'Mobile' : 'Desktop';
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /OPR\/|Opera/.test(ua)
      ? 'Opera'
      : /SamsungBrowser/.test(ua)
        ? 'Samsung Internet'
        : /Firefox|FxiOS/.test(ua)
          ? 'Firefox'
          : /Chrome|CriOS/.test(ua)
            ? 'Chrome'
            : /Safari/.test(ua)
              ? 'Safari'
              : 'Other';
  const os = /Windows/.test(ua)
    ? 'Windows'
    : /Android/.test(ua)
      ? 'Android'
      : /iPhone|iPad|iPod/.test(ua)
        ? 'iOS'
        : /Mac OS X/.test(ua)
          ? 'macOS'
          : /Linux/.test(ua)
            ? 'Linux'
            : 'Other';
  return { device, browser, os };
};

// Where the session came from: ?utm_source, else the referring site, else Direct.
// Stored per session so every page view in a visit carries the original source.
const sessionSource = () =>
  stored(
    () => window.sessionStorage,
    'binud:source',
    () => {
      const utm = new URLSearchParams(window.location.search).get('utm_source');
      if (utm) return utm.slice(0, 100);
      try {
        const ref = document.referrer && new URL(document.referrer);
        if (ref && ref.hostname !== window.location.hostname) return ref.hostname.replace(/^www\./, '').slice(0, 100);
      } catch {
        // Malformed referrer: treat as direct.
      }
      return 'Direct';
    }
  );

let last = { path: '', at: 0 };

export const trackVisit = (path) => {
  if (path.startsWith('/admin') || isExcluded() || BOT.test(navigator.userAgent)) return;

  // Guard against the same view being logged twice in quick succession (double renders).
  const now = Date.now();
  if (last.path === path && now - last.at < 1000) return;
  last = { path, at: now };

  const visit = {
    path: path.slice(0, 200),
    source: sessionSource(),
    ...parseAgent(navigator.userAgent),
    screen: `${window.screen.width}x${window.screen.height}`,
    lang: (navigator.language || '').slice(0, 20),
    tz: (Intl.DateTimeFormat().resolvedOptions().timeZone || '').slice(0, 60),
    host: window.location.hostname.slice(0, 100),
    visitorId: stored(() => window.localStorage, 'binud:visitor', randomId).slice(0, 40),
    sessionId: stored(() => window.sessionStorage, 'binud:session', randomId).slice(0, 40),
  };

  // Firebase is loaded on demand so it stays out of the main bundle every visitor downloads.
  Promise.all([import('firebase/firestore'), import('../config/firebase')])
    .then(([{ addDoc, collection, serverTimestamp }, { db }]) =>
      addDoc(collection(db, 'visits'), { ...visit, createdAt: serverTimestamp() })
    )
    .catch(() => {
      // Analytics must never break the site.
    });
};

// Logs a page view every time the route changes.
export const VisitTracker = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    trackVisit(pathname);
  }, [pathname]);
  return null;
};
